import { AGGREGATE_NAMES } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_GetSummary,
    SchemaUserResolver_GetAclPermissions,
} from 'cpool-tables/backend-resolvers';
import { getAssignedUserSlots } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { AssignedUserLine } from './AssignedUserLine';
import { getAdviserName } from './getAdviserName';
import { useLeadAdvisers } from './useLeadAdvisers';
import { useSetActiveUserMutation } from './useSetActiveUserMutation';

export type AssignedUsersLead = Pick<
    SchemaLoanRequestResolver_GetSummary[number],
    'activeUserId' | 'id' | 'slotCategories' | 'userCategories' | 'userIds'
>;

type Props = {
    lead: AssignedUsersLead;
    permissions?: SchemaUserResolver_GetAclPermissions;
};

export const AssignedUserSlots: React.FC<Props> = ({ lead, permissions }) => {
    const { t } = useTranslation();
    const { data: advisers = [], isError: advisersFailed, isLoading } = useLeadAdvisers(lead);
    const { isPending, mutate } = useSetActiveUserMutation(lead.id);
    const slots = getAssignedUserSlots(lead);
    // without adviser names a set-active toast could only name the unknown fallback
    const canSetActive = !advisersFailed && !!permissions?.commands[AGGREGATE_NAMES.LOAN_REQUEST].setActiveUser;

    if (!slots.length)
        return (
            <span className="text-sm text-midnight" data-testid="assigned-users-empty">
                {t('LEADS.ASSIGNED_USERS.NONE')}
            </span>
        );
    // no unknown-user flash while the names load
    if (isLoading) return null;

    const nameOf = (userId: string) => getAdviserName(advisers, userId, t('LEADS.ASSIGNED_USERS.UNKNOWN_USER'));

    return (
        <div className="flex flex-col gap-1 min-w-0" data-testid="assigned-user-slots">
            {slots.map((slot) => (
                <AssignedUserLine
                    canSetActive={canSetActive}
                    disabled={isPending}
                    key={slot.userId}
                    name={nameOf(slot.userId)}
                    onSetActive={() => mutate({ name: nameOf(slot.userId), userId: slot.userId })}
                    slot={slot}
                />
            ))}
        </div>
    );
};
